import {
  Card,
  Spin,
  Modal,
  Select,
  Button,
  message,
  Divider,
  Popconfirm,
} from 'antd'
import { useState } from 'react'
import { EditOutlined, TagsOutlined, PlusOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import './TreasureHunterCards.css'
import {
  getAllGameTags,
  getTagsByGameId,
  addTagsByGame,
  deleteGameByTag,
} from 'src/api/tag'

const TreasureHunterCard = ({
  id,
  img,
  name,
  desc,
  instruction,
  title,
  descFr,
  instructionFr,
  titleFr,
  descAr,
  instructionAr,
  titleAr,
  enabled,
  category,
  accessLevels,
  thumbnailImage,
  complex,
  retentionDepth,
  monetizationModel,
  tier
}) => {
  const navigate = useNavigate()

  const [tagsModalVisible, setTagsModalVisible] = useState(false)

  const [linkedTags, setLinkedTags] = useState([])
  const [allTags, setAllTags] = useState([])
  const [selectedTagIds, setSelectedTagIds] = useState([])

  const [loadingTags, setLoadingTags] = useState(false)
  const [addingTags, setAddingTags] = useState(false)

  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    total: 0,
  })
  const [tagSearch, setTagSearch] = useState('')

  const [dataLoaded, setDataLoaded] = useState(true)

  const params = new URLSearchParams()
  params.append('img', img)
  params.append('name', name)
  params.append('desc', desc)
  params.append('instruction', instruction)
  params.append('title', title)
  params.append('descFr', descFr)
  params.append('instructionFr', instructionFr)
  params.append('titleFr', titleFr)
  params.append('descAr', descAr)
  params.append('instructionAr', instructionAr)
  params.append('titleAr', titleAr)
  params.append('enabled', enabled)
  params.append('category', category)
  params.append('thumbnailImage', thumbnailImage)
  params.append('complex', complex)
  params.append('retentionDepth', retentionDepth)
  params.append('monetizationModel', monetizationModel)
  params.append('tier', tier)
  if (accessLevels != null && accessLevels != undefined) {
    accessLevels.forEach((accessLevel) =>
      params.append('accessLevels', accessLevel)
    )
  }
  const handleClickOpen = () => {
    navigate(`/treasure-hunter/edit/${id}?${params.toString()}`)
  }
  const fetchLinkedTags = async (page = 0) => {
    try {
      setLoadingTags(true)
      const res = await getTagsByGameId(id, page, 10000)
      setLinkedTags(res.data || [])
    } catch {
      message.error('Failed to load game tags')
    } finally {
      setLoadingTags(false)
    }
  }

  const fetchAllTags = async (page = 0, name?: string) => {
    const res = await getAllGameTags(page, pagination.size, name, null)

    setAllTags(res.data || [])
    setPagination((p) => ({
      ...p,
      page,
      total: res.total_items,
    }))
  }

  const openManageTags = async () => {
    setTagsModalVisible(true)
    setSelectedTagIds([])
    await fetchLinkedTags()
    await fetchAllTags(0)
  }

  const handleRemoveTag = async (tagId: string) => {
    try {
      await deleteGameByTag(tagId, id)
      message.success('Tag removed')
      fetchLinkedTags()
    } catch {
      message.error('Failed to remove tag')
    }
  }

  const handleAddTags = async () => {
    if (selectedTagIds.length === 0) return

    try {
      setAddingTags(true)
      await addTagsByGame(selectedTagIds, id)
      message.success('Tags added successfully')
      setSelectedTagIds([])
      await fetchLinkedTags()
    } catch {
      message.error('Failed to add tags')
    } finally {
      setAddingTags(false)
    }
  }

  return (
    <>
      <div>
        {dataLoaded ? (
          <Card
            hoverable
            className={`card-container ${enabled ? '' : 'faded-card'}`}
            cover={
              <div className="card-cover">
                <img alt={title} src={img} className="card-image" />
                <div className="card-icons">
                  <TagsOutlined
                    onClick={openManageTags}
                    className="tags-icon"
                    title="Manage Tags"
                  />
                  <EditOutlined
                    onClick={handleClickOpen}
                    className="edit-icon"
                    title="Edit Game"
                  />
                </div>
              </div>
            }
          >
            <Card.Meta
              title={title}
              description={<div className="card-description">{desc}</div>}
            />
          </Card>
        ) : (
          <div className="spinner-container">
            <Spin size="large" />
          </div>
        )}
      </div>
      <Modal
        title={`Manage Tags - ${title}`}
        visible={tagsModalVisible}
        onCancel={() => setTagsModalVisible(false)}
        footer={null}
        width={650}
        destroyOnClose
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <Select
            mode="multiple"
            showSearch
            placeholder="Search tags by name"
            style={{ flex: 1 }}
            value={selectedTagIds}
            onChange={setSelectedTagIds}
            filterOption={false}
            onSearch={(value) => {
              setTagSearch(value)
              fetchAllTags(0, value)
            }}
            options={allTags.map((t) => ({
              label: t.nameEn,
              value: t.id,
            }))}
          />

          <Button
            type="primary"
            onClick={handleAddTags}
            loading={addingTags}
            disabled={selectedTagIds.length === 0}
            className="btn-primary"
            style={{ width: 100 }}
            icon={<PlusOutlined />}
          >
            Add
          </Button>
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          {linkedTags.map((tag) => (
            <Popconfirm
              key={tag.id}
              title="Remove tag?"
              okText="Remove"
              cancelText="Cancel"
              onConfirm={() => handleRemoveTag(tag.id)}
            >
              <div className="game-pill clickable">
                {tag.nameEn}
                <span className="pill-cross">×</span>
              </div>
            </Popconfirm>
          ))}
        </div>

        <Divider />
      </Modal>
    </>
  )
}

export default TreasureHunterCard
